import React, { useEffect, useMemo, useState } from "react";
import {
  FaArchive,
  FaCheck,
  FaClone,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  DEFAULT_TASK_FORM,
  TASK_LABEL_COLORS,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
} from "../data/tasksKanbanData";
import {
  buildTaskPayload,
  formatDateTimeFR,
  getPriorityBadge,
  getStatusBadge,
} from "../utils/tasksKanbanUtils";

export default function TaskDetailModal({
  task,
  associations,
  productsApps,
  saving,
  onClose,
  onUpdate,
  onToggleDone,
  onDuplicate,
  onArchive,
  onDelete,
}) {
  const [formData, setFormData] = useState(DEFAULT_TASK_FORM);

  useEffect(() => {
    if (task) {
      setFormData({
        ...DEFAULT_TASK_FORM,
        ...task,
        asso_id: task.asso_id || "",
        product_app_id: task.product_app_id || "",
        due_date: task.due_date ? String(task.due_date).slice(0, 10) : "",
        labels_json: Array.isArray(task.labels_json) ? task.labels_json : [],
        checklist_json: Array.isArray(task.checklist_json)
          ? task.checklist_json
          : [],
      });
    }
  }, [task]);

  const status = useMemo(
    () => getStatusBadge(formData.statut),
    [formData.statut],
  );
  const priority = useMemo(
    () => getPriorityBadge(formData.priority),
    [formData.priority],
  );

  if (!task) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleQuillChange = (value) => {
    setFormData((current) => ({
      ...current,
      contenu: value,
    }));
  };

  const handleChecklistChange = (index, field, value) => {
    setFormData((current) => {
      const checklist = [...(current.checklist_json || [])];

      checklist[index] = {
        ...checklist[index],
        [field]: value,
      };

      return {
        ...current,
        checklist_json: checklist,
      };
    });
  };

  const addChecklistItem = () => {
    setFormData((current) => ({
      ...current,
      checklist_json: [
        ...(current.checklist_json || []),
        {
          id: Date.now(),
          text: "",
          done: false,
        },
      ],
    }));
  };

  const removeChecklistItem = (index) => {
    setFormData((current) => ({
      ...current,
      checklist_json: (current.checklist_json || []).filter(
        (_, itemIndex) => itemIndex !== index,
      ),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await onUpdate(task.id, buildTaskPayload(formData));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-center bg-black/70 px-3 py-5 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-40px)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-slate-100 shadow-2xl">
        <div className="flex items-center justify-between bg-[#f5d719] px-5 py-4">
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full border px-2 py-0.5 text-xs font-bold ${status.className}`}
              >
                {status.label}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-bold ${priority.className}`}
              >
                {priority.label}
              </span>
            </div>

            <h2 className="truncate text-xl font-bold text-slate-900">
              {task.titre}
            </h2>
            <p className="text-xs font-medium text-slate-700">
              Créée le {formatDateTimeFR(task.date_creation)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-700 transition hover:bg-black/10"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="grid gap-0 lg:grid-cols-[1fr_320px]">
            <main className="space-y-5 p-5">
              {formData.cover_url && (
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <img
                    src={formData.cover_url}
                    alt={formData.titre}
                    className="max-h-72 w-full object-cover"
                  />
                </div>
              )}

              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <label className="mb-1 block text-sm font-bold text-slate-700">
                  Titre
                </label>
                <input
                  type="text"
                  name="titre"
                  value={formData.titre || ""}
                  onChange={handleChange}
                  required
                  className="min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                />
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <label className="mb-2 block text-sm font-bold text-slate-700">
                  Description
                </label>
                <ReactQuill
                  value={formData.contenu || ""}
                  onChange={handleQuillChange}
                  className="rounded-xl bg-white"
                />
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-slate-800">
                    Checklist
                  </h3>
                  <button
                    type="button"
                    onClick={addChecklistItem}
                    className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200"
                  >
                    Ajouter une ligne
                  </button>
                </div>

                <div className="space-y-2">
                  {(formData.checklist_json || []).map((item, index) => (
                    <div
                      key={item.id || index}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(item.done)}
                        onChange={(event) =>
                          handleChecklistChange(
                            index,
                            "done",
                            event.target.checked,
                          )
                        }
                        className="h-5 w-5"
                      />

                      <input
                        type="text"
                        value={item.text || ""}
                        onChange={(event) =>
                          handleChecklistChange(
                            index,
                            "text",
                            event.target.value,
                          )
                        }
                        placeholder="Élément de checklist"
                        className="min-h-[38px] flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-violet-400"
                      />

                      <button
                        type="button"
                        onClick={() => removeChecklistItem(index)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-red-500 transition hover:bg-red-50"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}

                  {(!formData.checklist_json ||
                    formData.checklist_json.length === 0) && (
                    <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500">
                      Aucune checklist pour cette carte.
                    </p>
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-bold text-slate-800">
                  Activité récente
                </h3>

                <div className="space-y-2">
                  {(task.activity_json || []).slice(0, 8).map((activity) => (
                    <div
                      key={activity.id}
                      className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600"
                    >
                      <div className="font-bold">{activity.message}</div>
                      <div className="text-xs text-slate-400">
                        {formatDateTimeFR(activity.created_at)}
                      </div>
                    </div>
                  ))}

                  {(!task.activity_json || task.activity_json.length === 0) && (
                    <p className="text-sm text-slate-500">
                      Aucune activité enregistrée.
                    </p>
                  )}
                </div>
              </section>
            </main>

            <aside className="space-y-4 border-t border-slate-200 bg-slate-50 p-5 lg:border-l lg:border-t-0">
              <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Informations
                </h3>

                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700">
                      Application
                    </label>
                    <select
                      name="product_app_id"
                      value={formData.product_app_id || ""}
                      onChange={handleChange}
                      className="min-h-[42px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    >
                      <option value="">Sans application</option>
                      {[...(productsApps || [])]
                        .sort((a, b) =>
                          String(a.nom_app).localeCompare(String(b.nom_app)),
                        )
                        .map((app) => (
                          <option key={app.id} value={app.id}>
                            {app.nom_app}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700">
                      Association
                    </label>
                    <select
                      name="asso_id"
                      value={formData.asso_id || ""}
                      onChange={handleChange}
                      className="min-h-[42px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    >
                      <option value="">Aucune association</option>
                      {[...(associations || [])]
                        .sort((a, b) =>
                          String(a.nom_asso).localeCompare(String(b.nom_asso)),
                        )
                        .map((association) => (
                          <option key={association.id} value={association.id}>
                            {association.nom_asso}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700">
                      Catégorie
                    </label>
                    <input
                      type="text"
                      name="categorie"
                      value={formData.categorie || ""}
                      onChange={handleChange}
                      className="min-h-[42px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700">
                      Statut
                    </label>
                    <select
                      name="statut"
                      value={formData.statut || "A faire"}
                      onChange={handleChange}
                      className="min-h-[42px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    >
                      {TASK_STATUS_OPTIONS.map((option) => (
                        <option
                          key={`${option.value}-${option.label}`}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700">
                      Priorité
                    </label>
                    <select
                      name="priority"
                      value={formData.priority || "NORMAL"}
                      onChange={handleChange}
                      className="min-h-[42px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    >
                      {TASK_PRIORITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700">
                      Échéance
                    </label>
                    <input
                      type="date"
                      name="due_date"
                      value={formData.due_date || ""}
                      onChange={handleChange}
                      className="min-h-[42px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700">
                      URL image de couverture
                    </label>
                    <input
                      type="text"
                      name="cover_url"
                      value={formData.cover_url || ""}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="min-h-[42px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none"
                    />
                  </div>
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Couleur label
                </h3>
                <div className="flex flex-wrap gap-2">
                  {TASK_LABEL_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() =>
                        setFormData((current) => ({
                          ...current,
                          label_color: color,
                        }))
                      }
                      className={`h-8 w-12 rounded-lg border-2 ${
                        formData.label_color === color
                          ? "border-slate-900"
                          : "border-transparent"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </section>

              <section className="space-y-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[#4b2a8f] px-4 py-2 text-sm font-bold text-white shadow-lg transition hover:bg-[#3d2177] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSave />
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </button>

                <button
                  type="button"
                  onClick={() => onToggleDone(task)}
                  className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-700 transition hover:bg-emerald-200"
                >
                  <FaCheck />
                  Terminer / rouvrir
                </button>

                <button
                  type="button"
                  onClick={() => onDuplicate(task)}
                  className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-blue-100 px-4 py-2 text-sm font-bold text-blue-700 transition hover:bg-blue-200"
                >
                  <FaClone />
                  Dupliquer
                </button>

                <button
                  type="button"
                  onClick={() => onArchive(task)}
                  className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-slate-200 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-300"
                >
                  <FaArchive />
                  Archiver
                </button>

                <button
                  type="button"
                  onClick={() => onDelete(task)}
                  className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-xl bg-red-100 px-4 py-2 text-sm font-bold text-red-700 transition hover:bg-red-200"
                >
                  <FaTrash />
                  Supprimer
                </button>
              </section>
            </aside>
          </div>
        </form>
      </div>
    </div>
  );
}
